import { useEffect, useMemo, useState } from "react";
import { Building2, CheckCircle2, Copy, Download, FileSignature, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { FALLBACK_CONTENT, fmtPrice } from "@/lib/content";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { SignatoriesFields } from "@/components/landing/order/SignatoriesFields";
import { fmtDate } from "@/components/landing/CompanyLookup";
import { isNip, isPesel } from "@/lib/validate";

const MODES = [
  { id: "existing", label: "Istniejący podmiot (NIP / GUS)" },
  { id: "new_jdg", label: "Rejestrowana JDG" },
  { id: "new_krs_s24", label: "Rejestrowany podmiot KRS — S24" },
  { id: "new_krs_deed", label: "Rejestrowany podmiot KRS — akt założycielski" },
];

const LEGAL_FORMS = [
  ["jdg", "Jednoosobowa działalność gospodarcza"], ["sc", "Spółka cywilna"], ["spj", "Spółka jawna"],
  ["spp", "Spółka partnerska"], ["spk", "Spółka komandytowa"], ["spka", "Spółka komandytowo-akcyjna"],
  ["spzoo", "Spółka z o.o."], ["psa", "Prosta spółka akcyjna"], ["sa", "Spółka akcyjna"],
  ["org", "Spółka w organizacji"], ["spoldzielnia", "Spółdzielnia"], ["fundacja", "Fundacja"],
  ["stowarzyszenie", "Stowarzyszenie"], ["oddzial", "Oddział przedsiębiorcy zagranicznego"], ["other", "Inna"],
];

const EEA = [["PL", "Polska"], ["AT", "Austria"], ["BE", "Belgia"], ["BG", "Bułgaria"], ["HR", "Chorwacja"],
  ["CY", "Cypr"], ["CZ", "Czechy"], ["DK", "Dania"], ["EE", "Estonia"], ["FI", "Finlandia"], ["FR", "Francja"],
  ["DE", "Niemcy"], ["GR", "Grecja"], ["HU", "Węgry"], ["IE", "Irlandia"], ["IT", "Włochy"], ["LV", "Łotwa"],
  ["LT", "Litwa"], ["LU", "Luksemburg"], ["MT", "Malta"], ["NL", "Niderlandy"], ["PT", "Portugalia"],
  ["RO", "Rumunia"], ["SK", "Słowacja"], ["SI", "Słowenia"], ["ES", "Hiszpania"], ["SE", "Szwecja"],
  ["IS", "Islandia"], ["LI", "Liechtenstein"], ["NO", "Norwegia"]];

const PERIODS = [
  ["monthly", "1 miesiąc", "price_monthly"], ["3", "3 miesiące z góry", "total_3"],
  ["6", "6 miesięcy z góry", "total_6"], ["12", "12 miesięcy z góry", "total_12"],
  ["24", "24 miesiące z góry", "total_24"],
];

const field = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/40";
const lbl = "text-xs font-semibold text-muted-foreground mb-1.5 block";

export function OrderGenerator({ context = "admin" }) {
  const [content, setContent] = useState(null);
  const [f, setF] = useState({
    entity_mode: "existing", package_id: "smart", billing: "12", amount_override: "",
    company: "", legal_form: "spzoo", nip: "", krs: "", registered_address: "",
    representative: "", pesel: "", email: "", phone: "", mailing_address: "", mailing_country: "PL",
    start_date: "", notes: "", owu_consent: false, send_client_email: false,
  });
  const [gus, setGus] = useState({ loading: false, data: null });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState(false);
  const [signatories, setSignatories] = useState([{ name: "", role: "" }]);

  const isKrs = f.entity_mode !== "new_jdg" && f.legal_form && f.legal_form !== "jdg";
  const isSc = f.legal_form === "sc";
  const sigProps = isSc ? { max: 4, label: "Wspólnicy spółki cywilnej (maks. 4) *", addLabel: "Dodaj wspólnika", defaultRole: "Wspólnik", showRepresents: true } : {};
  const signatoriesStr = signatories.filter((s) => s.name.trim()).map((s) => `${s.name.trim()}${s.role ? ` – ${s.role}` : ""}`).join(", ");

  useEffect(() => { api.get("/content").then((r) => setContent(r.data)).catch(() => setContent(FALLBACK_CONTENT)); }, []);

  const packages = content?.packages || FALLBACK_CONTENT.packages;
  const pkg = useMemo(() => packages.find((p) => p.id === f.package_id) || packages[0], [packages, f.package_id]);
  const b24 = content ? content.billing_24_enabled : false;
  const availPeriods = PERIODS.filter(([id, , pf]) => (id !== "24" || b24) && String(pkg?.[pf] || "").trim());

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => { const m = isSc ? 4 : 2; setSignatories((p) => (p.length > m ? p.slice(0, m) : p)); }, [isSc]);

  // auto-fill the net amount suggestion whenever package/period changes (editable)
  useEffect(() => {
    const pf = PERIODS.find(([id]) => id === f.billing)?.[2];
    const net = String(pkg?.[pf] || "").trim();
    set("amount_override", net);
  }, [f.package_id, f.billing, pkg]); // eslint-disable-line react-hooks/exhaustive-deps

  const mode = f.entity_mode;
  const showNip = mode === "existing";
  const showPesel = mode === "new_jdg";
  const showMailing = mode !== "new_krs_s24";
  const showOwu = mode === "new_krs_s24";
  const companyLocked = mode === "existing" && !manual;

  const runGus = async () => {
    const digits = (f.nip || "").replace(/\D/g, "");
    if (!isNip(digits)) { toast.error("Podaj poprawny NIP (10 cyfr, suma kontrolna)."); return; }
    setGus({ loading: true, data: null });
    try {
      const r = await api.get(`/company/${digits}`);
      if (!r.data.found) { setGus({ loading: false, data: null }); setManual(true); toast.error("Nie znaleziono firmy w GUS — wpisz dane ręcznie."); return; }
      setF((s) => ({ ...s, company: r.data.name || s.company, legal_form: r.data.legal_form || s.legal_form,
        krs: r.data.krs || s.krs, registered_address: r.data.address || s.registered_address }));
      setManual(false);
      setGus({ loading: false, data: r.data });
    } catch (e) {
      setGus({ loading: false, data: null });
      setManual(true);
      toast.error(e.response?.data?.detail || "Rejestr GUS niedostępny — wpisz dane ręcznie.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "existing" && !isNip(f.nip)) { toast.error("Podaj poprawny NIP (10 cyfr, suma kontrolna)."); return; }
    if (mode === "new_jdg" && !isPesel(f.pesel)) { toast.error("Podaj poprawny PESEL (11 cyfr, suma kontrolna i data)."); return; }
    if (isKrs && !signatories.some((s) => s.name.trim())) { toast.error("Podaj co najmniej jedną osobę podpisującą."); return; }
    setBusy(true); setResult(null);
    try {
      const payload = { ...f, package_name: pkg?.name || "", proxies: [],
        signatories: isKrs ? signatories.filter((s) => s.name.trim()).map((s) => ({ name: s.name.trim(), role: s.role || "", represents: !!s.represents })) : [],
        representative: isKrs ? signatoriesStr : f.representative };
      const r = await api.post("/internal/orders", payload);
      setResult(r.data);
      toast.success("Dokumenty wygenerowane.");
    } catch (er) {
      const d = er.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Nie udało się wygenerować dokumentów.");
    } finally { setBusy(false); }
  };

  const copyPay = () => { navigator.clipboard?.writeText(result.pay_url); toast.success("Link PayU skopiowany."); };

  return (
    <div data-testid={`order-generator-${context}`} className="bg-card border border-border rounded-2xl p-5 sm:p-7">
      <div className="flex items-center gap-2.5 mb-1">
        <span className="w-9 h-9 rounded-xl bg-brand-navy text-white flex items-center justify-center shrink-0"><FileSignature size={18} /></span>
        <h3 className="font-display font-bold text-lg">Generator umowy i proformy</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-6 leading-relaxed">Wypełnij dane klienta (możesz pobrać je z GUS lub wpisać ręcznie), a system wygeneruje umowę i proformę z linkiem do płatności PayU. Pliki pobierzesz na komputer; opcjonalnie wyślemy je mailem do klienta.</p>

      <form onSubmit={submit} className="space-y-6" data-testid="order-generator-form">
        {/* rodzaj + pakiet */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={lbl}>Rodzaj zgłoszenia</label>
            <select data-testid="og-mode" className={field} value={f.entity_mode} onChange={(e) => set("entity_mode", e.target.value)}>
              {MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Pakiet</label>
            <select data-testid="og-package" className={field} value={f.package_id} onChange={(e) => set("package_id", e.target.value)}>
              {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>

        {mode !== "new_krs_deed" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Okres rozliczeniowy</label>
              <select data-testid="og-billing" className={field} value={f.billing} onChange={(e) => set("billing", e.target.value)}>
                {availPeriods.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Kwota netto (PLN) — edytowalna</label>
              <Input data-testid="og-amount" value={f.amount_override} onChange={(e) => set("amount_override", e.target.value)} placeholder="np. 744" className="bg-background" />
              <p className="text-[11px] text-muted-foreground mt-1">Z cennika: {fmtPrice(pkg?.[PERIODS.find(([id]) => id === f.billing)?.[2]] || "") || "—"} zł netto. VAT 23% doliczany automatycznie.</p>
            </div>
          </div>
        )}

        {/* dane firmy */}
        <div className="border-t border-border pt-5">
          <div className="flex items-center justify-between mb-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Dane podmiotu</p>
            {mode === "existing" && (
              <button type="button" data-testid="og-manual-toggle" onClick={() => setManual((v) => !v)} className="text-[11px] font-semibold text-brand-teal hover:text-brand-tealDark transition-colors">
                {manual ? "Zablokuj (dane z GUS)" : "Wpisz dane ręcznie"}
              </button>
            )}
          </div>
          {showNip && (
            <div className="mb-4">
              <label className={lbl}>NIP</label>
              <div className="flex gap-2">
                <Input data-testid="og-nip" value={f.nip} onChange={(e) => set("nip", e.target.value)} placeholder="10 cyfr" className="bg-background" />
                <button type="button" data-testid="og-gus-button" onClick={runGus} disabled={gus.loading} className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-lg px-3 hover:bg-muted transition-colors disabled:opacity-50">
                  {gus.loading ? <Loader2 size={13} className="animate-spin" /> : <Building2 size={13} />} Pobierz z GUS
                </button>
              </div>
              {gus.data && (
                <div data-testid="og-gus-result" className="mt-2 rounded-lg border border-brand-teal/25 bg-brand-teal/5 px-3 py-2 text-xs leading-relaxed">
                  <p className="inline-flex items-start gap-1.5 font-semibold text-foreground"><CheckCircle2 size={13} className="text-brand-teal shrink-0 mt-0.5" /><span>Pobrano z GUS: {gus.data.name}</span></p>
                  <ul className="mt-1 space-y-0.5 text-muted-foreground pl-[18px]">
                    {gus.data.vat_status && <li>VAT: {gus.data.vat_status}</li>}
                    {gus.data.start_date && <li>Działalność od: {fmtDate(gus.data.start_date)}</li>}
                    {(gus.data.vat_checked || gus.data.gus_checked) && <li className="text-brand-teal/90">Dane z rejestru na dzień {fmtDate(gus.data.vat_checked) || fmtDate(new Date().toISOString())}</li>}
                    {gus.data.ended && <li className="text-brand-red font-semibold">Uwaga: działalność wykreślona lub zakończona</li>}
                  </ul>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>{mode === "new_jdg" ? "Imię i nazwisko / firma" : "Nazwa firmy"}</label>
              <Input data-testid="og-company" value={f.company} onChange={(e) => set("company", e.target.value)} readOnly={companyLocked} required className={`bg-background ${companyLocked ? "opacity-80 cursor-not-allowed" : ""}`} />
              {companyLocked && <p className="text-[11px] text-muted-foreground mt-1">Nazwę pobieramy z rejestru REGON. Aby wpisać ręcznie, użyj „Wpisz dane ręcznie".</p>}
            </div>
            <div>
              <label className={lbl}>Forma prawna</label>
              <select data-testid="og-legal-form" className={field} value={f.legal_form} onChange={(e) => set("legal_form", e.target.value)}>
                {LEGAL_FORMS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            {showNip && (
              <div>
                <label className={lbl}>KRS (jeśli dotyczy)</label>
                <Input data-testid="og-krs" value={f.krs} onChange={(e) => set("krs", e.target.value)} className="bg-background" />
              </div>
            )}
            {showPesel && (
              <div>
                <label className={lbl}>PESEL</label>
                <Input data-testid="og-pesel" value={f.pesel} onChange={(e) => set("pesel", e.target.value)} placeholder="11 cyfr" className="bg-background" />
              </div>
            )}
            {showNip && (
              <div className="md:col-span-2">
                <label className={lbl}>Adres siedziby / rejestrowy</label>
                <Input data-testid="og-registered-address" value={f.registered_address} onChange={(e) => set("registered_address", e.target.value)} readOnly={companyLocked} className={`bg-background ${companyLocked ? "opacity-80 cursor-not-allowed" : ""}`} />
              </div>
            )}
          </div>
        </div>

        {/* dane kontaktowe */}
        <div className="border-t border-border pt-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-4">Dane kontaktowe</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Data rozpoczęcia</label>
              <Input data-testid="og-start-date" type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} className="bg-background" />
            </div>
            {isKrs ? (
              <div className="md:col-span-2">
                <SignatoriesFields signatories={signatories} onChange={setSignatories} {...sigProps} />
              </div>
            ) : (
              <div>
                <label className={lbl}>Osoba reprezentująca</label>
                <Input data-testid="og-representative" value={f.representative} onChange={(e) => set("representative", e.target.value)} className="bg-background" />
              </div>
            )}
            <div>
              <label className={lbl}>E-mail klienta</label>
              <Input data-testid="og-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} required className="bg-background" />
            </div>
            <div>
              <label className={lbl}>Telefon</label>
              <Input data-testid="og-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} required className="bg-background" />
            </div>
            {showMailing && (
              <>
                <div>
                  <label className={lbl}>Adres korespondencyjny</label>
                  <Input data-testid="og-mailing-address" value={f.mailing_address} onChange={(e) => set("mailing_address", e.target.value)} className="bg-background" />
                </div>
                <div>
                  <label className={lbl}>Kraj korespondencji</label>
                  <select data-testid="og-mailing-country" className={field} value={f.mailing_country} onChange={(e) => set("mailing_country", e.target.value)}>
                    {EEA.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </>
            )}
            <div className="md:col-span-2">
              <label className={lbl}>Uwagi (opcjonalnie)</label>
              <Textarea data-testid="og-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="bg-background" />
            </div>
          </div>
        </div>

        {showOwu && (
          <label className="flex items-start gap-2.5 text-sm cursor-pointer">
            <input type="checkbox" data-testid="og-owu" checked={f.owu_consent} onChange={(e) => set("owu_consent", e.target.checked)} className="mt-0.5" />
            <span>Klient akceptuje Ogólne Warunki Świadczenia Usług (wymagane dla S24).</span>
          </label>
        )}

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border pt-5">
          <label className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid="og-send-email-label">
            <Switch data-testid="og-send-email" checked={f.send_client_email} onCheckedChange={(v) => set("send_client_email", v)} />
            <span>Wyślij e-mail do klienta (umowa + proforma w załączniku, link „Zapłać”)</span>
          </label>
          <button type="submit" data-testid="og-submit" disabled={busy} className="inline-flex items-center gap-2 bg-brand-teal hover:bg-brand-tealDark disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-full text-sm transition-colors">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <FileSignature size={15} />} Generuj dokumenty
          </button>
        </div>
      </form>

      {result && (
        <div data-testid="order-generator-result" className="mt-7 border-t border-border pt-6">
          <h4 className="font-display font-bold text-base flex items-center gap-2"><CheckCircle2 size={18} className="text-brand-teal" /> Gotowe</h4>
          {result.proforma_number && <p className="text-sm text-muted-foreground mt-2">Numer proformy: <span className="font-semibold text-foreground">{result.proforma_number}</span>{result.amount_label && <> · do zapłaty: <span className="font-semibold text-foreground">{result.amount_label}</span></>}</p>}
          {result.mode === "new_krs_deed" && <p className="text-sm text-muted-foreground mt-2">Tryb „akt założycielski” — umowa i proforma nie są generowane automatycznie (klient przesyła skan aktu).</p>}

          {!!result.documents?.length && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="og-documents">
              {result.documents.map((d) => (
                <a key={d.url} href={d.url} data-testid={`og-doc-${d.name.includes("Umowa") ? "contract" : "proforma"}`} className="inline-flex items-center gap-2 text-sm font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors">
                  <Download size={14} /> {d.name}
                </a>
              ))}
            </div>
          )}

          {result.pay_url && (
            <div className="mt-4">
              <label className={lbl}>Link do płatności PayU (ważny 72 h)</label>
              <div className="flex gap-2">
                <Input data-testid="og-pay-url" readOnly value={result.pay_url} className="bg-background text-xs" />
                <button type="button" data-testid="og-copy-pay" onClick={copyPay} className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-lg px-3 hover:bg-muted transition-colors"><Copy size={13} /> Kopiuj</button>
                <a href={result.pay_url} target="_blank" rel="noopener noreferrer" data-testid="og-open-pay" className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold bg-brand-teal text-white rounded-lg px-3 py-2 hover:bg-brand-tealDark transition-colors">Otwórz</a>
              </div>
            </div>
          )}

          <p className="mt-4 text-xs inline-flex items-center gap-1.5 text-muted-foreground" data-testid="og-email-status">
            {result.email_sent ? <><Send size={13} className="text-brand-teal" /> E-mail wysłany do klienta.</> : "E-mail do klienta nie został wysłany (przełącznik był wyłączony)."}
          </p>
          <p className="mt-2 text-[11px] text-muted-foreground leading-relaxed">Zgodnie z zasadą „zero przechowywania” dane zamówienia znikają z serwera po kilku minutach — pobierz pliki teraz. Linki do pobrania działają 24 h, a link PayU 72 h.</p>
        </div>
      )}
    </div>
  );
}
